import { useCallback, useEffect, useRef, useState } from 'react';
import { getFilterData, getValidEntities, type FilterData } from '@/lib/nomenclature/appointmentRepo';
import { getTimetable, type TimetableInterval } from '../api/booking.api';
import { isFirstAvailableActive, NoSlotError, searchFirstAvailable } from './firstAvailable';
import { EMPTY_SELECTION, type BookingMode, type BookingSelection, type ResolvedSlot } from './types';

/** The five dropdown dimensions. Order is display-only — any may be picked first. */
const DIMENSIONS = ['city', 'location', 'specialty', 'service', 'doctor'] as const;
type Dimension = (typeof DIMENSIONS)[number];

/** The filterData facet that lists options for a given dimension. */
const FACET: Record<Dimension, keyof FilterData> = {
  city: 'cities',
  location: 'locations',
  specialty: 'specialties',
  service: 'services',
  doctor: 'doctors',
};

/** Is `id` still an offered option for `dim` in the given filter data? */
function facetHas(data: FilterData, dim: Dimension, id: number): boolean {
  return (data[FACET[dim]] as Array<{ id: number }>).some((o) => o.id === id);
}

const EMPTY_FILTER_DATA: FilterData = {
  cities: [],
  locations: [],
  specialties: [],
  services: [],
  doctors: [],
};

function selectionIds(s: BookingSelection) {
  return {
    cityId: s.city?.id ?? null,
    locationId: s.location?.id ?? null,
    specialtyId: s.specialty?.id ?? null,
    investigationId: s.service?.id ?? null,
    physicianId: s.doctor?.id ?? null,
  };
}

/** Apply single-option auto-selection (only fills empty fields). Returns null if nothing changed. */
function autoSelect(selection: BookingSelection, data: FilterData): BookingSelection | null {
  let changed = false;
  const next = { ...selection };
  if (!next.city && data.cities.length === 1) {
    next.city = data.cities[0]!;
    changed = true;
  }
  if (!next.location && data.locations.length === 1) {
    next.location = data.locations[0]!;
    changed = true;
  }
  if (!next.specialty && data.specialties.length === 1) {
    next.specialty = data.specialties[0]!;
    changed = true;
  }
  if (!next.service && data.services.length === 1) {
    next.service = data.services[0]!;
    changed = true;
  }
  if (!next.doctor && data.doctors.length === 1) {
    next.doctor = data.doctors[0]!;
    changed = true;
  }
  return changed ? next : null;
}

export interface ManualInterval extends TimetableInterval {
  entityId: string;
  physicianId: string | null;
}

const sameDate = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const monthStart = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);

export function useBooking() {
  const [selection, setSelection] = useState<BookingSelection>(EMPTY_SELECTION);
  const [filterData, setFilterData] = useState<FilterData>(EMPTY_FILTER_DATA);
  const [loadingFilters, setLoadingFilters] = useState(true);

  const [mode, setMode] = useState<BookingMode | null>(null);

  // First-available state.
  const [faStatus, setFaStatus] = useState<'idle' | 'searching' | 'found' | 'error'>('idle');
  const [faSlot, setFaSlot] = useState<ResolvedSlot | null>(null);
  const [faError, setFaError] = useState<string | null>(null);

  // Manual-selection state.
  const [month, setMonth] = useState<Date>(monthStart(new Date()));
  const [days, setDays] = useState<Awaited<ReturnType<typeof getTimetable>>>([]);
  const [loadingTimetable, setLoadingTimetable] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [manualInterval, setManualInterval] = useState<ManualInterval | null>(null);

  // Guards against out-of-order async results.
  const filterReq = useRef(0);
  const timetableReq = useRef(0);

  const resetResults = useCallback(() => {
    setFaStatus('idle');
    setFaSlot(null);
    setFaError(null);
    setSelectedDate(null);
    setManualInterval(null);
    setDays([]);
  }, []);

  /**
   * Recompute facets for `base` and commit. `pinned` is the dimension the user
   * just acted on — it is never pruned, so when a new pick conflicts with an
   * earlier one, the new pick wins and the stale selection is dropped. Other
   * selected values that are no longer offered get cleared; empty dimensions
   * with a single remaining option are auto-selected when `doAutoSelect`.
   */
  const applySelection = useCallback(
    async (base: BookingSelection, doAutoSelect: boolean, pinned: Dimension | null) => {
      const reqId = ++filterReq.current;
      setLoadingFilters(true);

      let current = base;
      let data = EMPTY_FILTER_DATA;
      for (let i = 0; i < DIMENSIONS.length + 2; i++) {
        data = await getFilterData(selectionIds(current));

        // Drop selections that conflict with the rest (keep the pinned one).
        let changed = false;
        const next = { ...current };
        for (const dim of DIMENSIONS) {
          if (dim === pinned) continue;
          const value = next[dim];
          if (value && !facetHas(data, dim, value.id)) {
            next[dim] = null;
            changed = true;
          }
        }
        if (changed) {
          current = next;
          continue;
        }

        if (doAutoSelect) {
          const auto = autoSelect(current, data);
          if (auto) {
            current = auto;
            continue;
          }
        }
        break;
      }

      if (reqId !== filterReq.current) return; // superseded
      setSelection(current);
      setFilterData(data);
      setLoadingFilters(false);
    },
    [],
  );

  // Initial load (auto-selects singletons, like the app on mount).
  useEffect(() => {
    void applySelection(EMPTY_SELECTION, true, null);
  }, [applySelection]);

  const selectField = useCallback(
    <D extends Dimension>(dim: D, value: BookingSelection[D]) => {
      setMonth(monthStart(new Date()));
      resetResults();
      setSelection((prev) => {
        const next: BookingSelection = { ...prev, [dim]: value };
        // No top-down clearing — any dimension may be chosen in any order.
        void applySelection(next, true, dim);
        return next;
      });
    },
    [applySelection, resetResults],
  );

  const clearField = useCallback(
    (dim: Dimension) => {
      setMonth(monthStart(new Date()));
      resetResults();
      setSelection((prev) => {
        const next: BookingSelection = { ...prev, [dim]: null };
        void applySelection(next, false, null);
        return next;
      });
    },
    [applySelection, resetResults],
  );

  const reset = useCallback(() => {
    setMode(null);
    setMonth(monthStart(new Date()));
    resetResults();
    setSelection(EMPTY_SELECTION);
    void applySelection(EMPTY_SELECTION, true, null);
  }, [applySelection, resetResults]);

  const canChooseMode = !!selection.city && !!selection.location && !!selection.specialty;
  const firstAvailableActive = isFirstAvailableActive(selection, filterData.services);
  const manualReady = !!selection.doctor && !!selection.service;

  // First-available search.
  const runFirstAvailable = useCallback(async () => {
    setFaStatus('searching');
    setFaSlot(null);
    setFaError(null);
    try {
      const slot = await searchFirstAvailable(selection);
      setFaSlot(slot);
      setFaStatus('found');
    } catch (e) {
      setFaError(e instanceof NoSlotError ? e.message : 'Nu am putut căuta. Încercați din nou.');
      setFaStatus('error');
    }
  }, [selection]);

  // Load the timetable for the active month in manual mode.
  useEffect(() => {
    if (mode !== 'manual' || !manualReady) return;
    const reqId = ++timetableReq.current;
    setLoadingTimetable(true);
    setSelectedDate(null);
    setManualInterval(null);

    void (async () => {
      try {
        const entities = await getValidEntities({
          locationId: selection.location?.id,
          specialtyId: selection.specialty?.id,
          physicianId: selection.doctor?.id,
          investigationId: selection.service?.id,
        });
        const entityIds = new Set(entities.map((e) => e.entityId));
        const result = await getTimetable({
          physicianId: selection.doctor?.physicianId,
          specialityId: selection.specialty?.specialityId,
          locationId: selection.location?.locationId,
          refMonth: `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}-01`,
        });
        if (reqId !== timetableReq.current) return;
        setDays(result.filter((d) => d.entityId && entityIds.has(d.entityId)));
      } catch {
        if (reqId !== timetableReq.current) return;
        setDays([]);
      } finally {
        if (reqId === timetableReq.current) setLoadingTimetable(false);
      }
    })();
  }, [mode, manualReady, month, selection.location, selection.specialty, selection.doctor, selection.service]);

  // Distinct, future calendar dates that have at least one available interval.
  const now = Date.now();
  const minBooking = now + 2 * 60 * 60 * 1000;
  const availableDates: Date[] = [];
  {
    const seen = new Set<number>();
    for (const day of [...days].sort((a, b) => a.day.getTime() - b.day.getTime())) {
      const hasSlot = day.intervals.some((i) => i.available !== false && i.start.getTime() >= minBooking);
      if (!hasSlot) continue;
      const dayStart = new Date(day.day.getFullYear(), day.day.getMonth(), day.day.getDate());
      const key = dayStart.getTime();
      if (seen.has(key)) continue;
      seen.add(key);
      availableDates.push(dayStart);
    }
  }

  // Intervals for the selected calendar date (tagged with their entity).
  const dateIntervals: ManualInterval[] = [];
  if (selectedDate) {
    for (const day of days) {
      if (!sameDate(day.day, selectedDate)) continue;
      for (const i of day.intervals) {
        if (i.available === false || i.start.getTime() < minBooking) continue;
        dateIntervals.push({ ...i, entityId: day.entityId, physicianId: day.physicianId });
      }
    }
    dateIntervals.sort((a, b) => a.start.getTime() - b.start.getTime());
  }

  const baseMonth = monthStart(new Date());
  const maxMonth = new Date(baseMonth.getFullYear(), baseMonth.getMonth() + 11, 1);
  const canPrevMonth = month.getTime() > baseMonth.getTime();
  const canNextMonth = month.getTime() < maxMonth.getTime();
  const goPrevMonth = useCallback(() => {
    setMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  }, []);
  const goNextMonth = useCallback(() => {
    setMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1));
  }, []);

  // Build the slot the confirmation step will book.
  let resolvedSlot: ResolvedSlot | null = null;
  if (mode === 'first') {
    resolvedSlot = faSlot;
  } else if (mode === 'manual' && manualInterval && selection.doctor && selection.location) {
    resolvedSlot = {
      doctorName: selection.doctor.name,
      physicianId: selection.doctor.physicianId,
      physicianDbId: selection.doctor.id,
      serviceName: selection.service?.name ?? '-',
      service: selection.service,
      entityId: manualInterval.entityId,
      day: manualInterval.start,
      start: manualInterval.start,
      end: manualInterval.end,
      locationName: selection.location.name,
      cityName: selection.city?.name ?? '',
    };
  }

  const chooseMode = useCallback(
    (next: BookingMode) => {
      setMode(next);
      resetResults();
    },
    [resetResults],
  );

  return {
    selection,
    filterData,
    loadingFilters,
    selectField,
    clearField,
    reset,

    canChooseMode,
    mode,
    chooseMode,
    firstAvailableActive,

    firstAvailable: {
      status: faStatus,
      slot: faSlot,
      error: faError,
      search: runFirstAvailable,
    },

    manual: {
      ready: manualReady,
      month,
      canPrevMonth,
      canNextMonth,
      goPrevMonth,
      goNextMonth,
      loadingTimetable,
      availableDates,
      selectedDate,
      selectDate: setSelectedDate,
      dateIntervals,
      selectedInterval: manualInterval,
      selectInterval: setManualInterval,
    },

    resolvedSlot,
  };
}

export type UseBooking = ReturnType<typeof useBooking>;
